import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSliderContext } from "./context";
import classNames from "classnames";

/**
 * Slider Dots component
 * This component renders the dots for the slider, allowing users to navigate
 * to specific slides. It also includes a progress bar that animates when
 * autoplay is enabled.
 */
export const SliderDots = () => {
  const { emblaApi, emblaOptions, slideDurations } = useSliderContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const progressBar = useRef(null);

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onSelectDot = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  // Restart animation after render — ref is guaranteed to point to the new
  // active dot's bar by the time this effect runs (post-commit).
  useEffect(() => {
    const bar = progressBar.current;
    if (!bar || !emblaOptions.autoplay) return;
    bar.classList.remove("animating");
    void bar.offsetWidth; // force reflow to restart CSS animation
    bar.classList.add("animating");
  }, [selectedIndex, scrollSnaps]);

  useEffect(() => {
    if (!emblaApi || !emblaOptions.active) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onInit).off("reInit", onSelect).off("select", onSelect);
    };
  }, [emblaApi]);

  /**
   * Don't render dots if the slider is in loop mode.
   * Don't render dots if there are no scroll snaps.
   * Don't render dots if autoplay is not enabled.
   */
  if (
    !scrollSnaps?.length ||
    emblaOptions.autoplay !== true ||
    !emblaOptions.active
  ) {
    return null;
  }

  return (
    <div className="embla__dots">
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          className={classNames("embla__dot", {
            "is-selected": index === selectedIndex,
            viewed: index < selectedIndex,
          })}
          onClick={() => onSelectDot(index)}
        >
          <span className="embla__dot-inner">
            <span
              className="progress-bar"
              ref={index === selectedIndex ? progressBar : null}
              style={{
                animationDuration:
                  (slideDurations?.[selectedIndex] ?? emblaOptions.autoplaySpeed) / 1000 + "s",
              }}
            />
          </span>
        </button>
      ))}
    </div>
  );
};
